import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';
import * as CookieConsent from 'vanilla-cookieconsent';
import { InstrumentCategories } from '../shared/charts/charts';
import { isInputError } from '../shared/functions';
import { parseToError } from '../shared/functions-angular';
import { ExpectedOwnedStrategy } from '../shared/owned-strategy-card/strategy';
import { ToastNotificationComponent } from '../shared/toast-notification/toast-notification.component';
import { AuthLinksService } from './auth-links.service';
import { AuthTokenService } from './auth-token.service';
import { ClientConsultService } from './client-consult.service';
import { LOCAL_STORAGE_KEYS } from './local-storage.service';
import { LoggerService } from './logger/logger.service';
import { LogoutService } from './logout.service';
import { ModalService } from './modal/modal.service';
import { PosthogService } from './posthog.service';
import { PushNotificationsService } from './push-notifications.service';
import { RedirectPreloaderService } from './redirect-preloader.service';
import { AccessToken, ApiClientAccountOut } from './rest-api/rest-api';
import { RestApiService } from './rest-api/rest-api.service';
import { ToastNotificationService } from './toast-notification/toast-notification.service';
import { UserNotificationService } from './user-notification/user-notification.service';
import { UserService } from './user/user.service';
import { RouteReloadService } from './reload-current-route';
import { NativeCheckService } from './native-check.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  public constructor(
    private restApiService: RestApiService,
    private toastService: ToastNotificationService,
    private redirectPreloaderService: RedirectPreloaderService,
    private authTokenService: AuthTokenService,
    private userNotificationService: UserNotificationService,
    private posthogService: PosthogService,
    private logoutService: LogoutService,
    private modalService: ModalService,
    private loggerService: LoggerService,
    private pushNotificationsService: PushNotificationsService,
    private clientConsultService: ClientConsultService,
    private route: ActivatedRoute,
    private authLinksService: AuthLinksService,
    private userService: UserService,
    private zone: NgZone,
    private router: Router,
    private routeReloadService: RouteReloadService,
    private nativeCheckService: NativeCheckService,
  ) {}


  public logIn(bcasKey: string): void {
    this.redirectPreloaderService.redirectingPreloader = true;

    this.userService.removeUserFromLocal().pipe(
      switchMap(() => {
        if (this.nativeCheckService.isNativePlatform()) {
          return this.restApiService.backendLogin(bcasKey, this.authLinksService.clientRedirectUri);
        }

        return this.restApiService.backendWebLogin(bcasKey, this.authLinksService.clientRedirectUri);
      })
    ).subscribe({
      next: (result) => {
        this.backendSuccesfullyAuthorized(result);
      },
      error: (error) => this.backendError(error),
    });
  }

  private backendSuccesfullyAuthorized(data: unknown): void {
    if (this.validateApiResponse(data)) {
      this.authTokenService.updateBackendTokens(data);
      this.fetchUserData();
    } else {
      this.backendError();
    }
  }

  private validateApiResponse(data: unknown): data is AccessToken {
    if (typeof data === 'object' &&
      data !== null &&
      'access_token' in data && typeof data.access_token === 'string' &&
      'expires_in' in data && typeof data.expires_in === 'number' &&
      'refresh_token' in data && typeof data.refresh_token === 'string' &&
      'token_type' in data && typeof data.token_type === 'string'
    ) {
        return true;
    }

    this.toastService.addNotification({
      title: 'toastNotifications.headers.authError',
      message: 'toastNotifications.messages.serverAuthError',
      type: 'warning',
      component: ToastNotificationComponent
    });

    return false;
  }

  public fetchUserData(): void {
    this.restApiService.getUserDetail().subscribe({
      next: this.handleUserDetailsData.bind(this),
      error: this.logRequestError.bind(this),
    });
  }

  private validateApiUserResponse(data: unknown): data is ApiClientAccountOut {
    // todo - add/sync with global currency DTO
    const availableCurrencies = [
      'EUR',
      'CZK',
    ];

    if (
      typeof data === 'object' &&
      data !== null &&
      'clientAccountId' in data && typeof data.clientAccountId === 'number' &&
      'currencyCode' in data && typeof data.currencyCode === 'string' &&
      availableCurrencies.includes(data['currencyCode'])
    ) {
        return true;
    }

    this.toastService.addNotification({
      title: 'toastNotifications.headers.authError',
      message: 'toastNotifications.messages.serverAuthError',
      type: 'warning',
      component: ToastNotificationComponent
    });

    return false;
  }

  private setDisclaimerShownAt(clientAccountId: number): void {
    this.restApiService.setDisclaimerShown(clientAccountId).subscribe({
      error: this.logRequestError.bind(this),
    });
  }

  public handleUserDetailsData(data: unknown, skipReload?: true): void {
    if(this.validateApiUserResponse(data)) {
      this.userService.loggedUser = {
        currencyCode: data.currencyCode as 'EUR' | 'CZK',
        clientAccountId: data.clientAccountId,
        maxRiskScore: data.maxRiskScore,
        canReadEnglish: data.canReadEnglish,
        createdDate: data.createdDate,
        managementType: data.managementType,
        language: 'cs-CZ', // not in api, CZ as default, user can probably change it in next version
        validContracts: data.validContracts,
        hasOpenOrder: data.hasOpenOrder,
        personalId: data.personalIdentificationNumber,
        name: data.firstName,
        surname: data.lastName,
        addresses: data.addresses,
        phoneNumber: data.phoneNumber,
        email: data.email,
        disclaimerShownAt: data.disclaimerShownAt,
        instrumentCategories: [...data.investmentQuestionnaire.instrumentCategories, InstrumentCategories.CASH],
        opfTransferAllowedAt: data.opfTransferAllowedAt,
        opfTransferAt: data.opfTransferAt,
      };

      this.clientConsultService.updateConsultData();

      if(CookieConsent.getUserPreferences().acceptedCategories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
        this.posthogService.startLog({
            email: data.email,
            client_id: data.bcasClientId,
            fullName: data.firstName + ' ' + data.lastName,
        });
      }

      this.redirectPreloaderService.redirectingPreloader = false;

      // /addition-redirect route is acessible without logged user, do not force user auth
      if(!this.router.url.includes('addition-redirect') && this.router.url.includes('logout')) {
        this.router.navigate(['/summary'], { replaceUrl: true });
      }

      this.userNotificationService.updateNotificationsCount();

      if(this.nativeCheckService.isNativePlatform()) {
        this.pushNotificationsService.initNotifications();
        if(!this.router.url.includes('addition-redirect') && !skipReload) {
          this.zone.run(() => {
            if(this.router.url.includes('login')) {
              this.router.navigate(['/summary'], { replaceUrl: true });
            } else {
              this.routeReloadService.reloadCurrent();
            }
          });
        }
      }

      // fix of https://monecois.atlassian.net/browse/MN-415 // https://www.notion.so/btech/MN-415-patn-vygenerovan-smlouvy-2-5MD-2MD-FE-6MD-47f5a316dc8244e9b218bf634a3ab69a#f705cedc876b4e038e338de3ac74cdc8
      if(!this.router.url.includes('addition-redirect')) {
        const unassignedContract = data.validContracts.find(contract => contract.unsignedAdditions.find((addition) => addition.type === 'MN_415'));
        if(unassignedContract) {
          const unsignedContractAddition = unassignedContract.unsignedAdditions.find((addition) => addition.type === 'MN_415')!;
          this.modalService.openAdditionalSignModal(unsignedContractAddition.frameworkContractAdditionId, unassignedContract.frameworkContractId);
        }
      }

      if (!data.disclaimerShownAt) {
        this.fetchClientPortfoliosAndStrategies(data.clientAccountId);
        this.modalService.openInitSignIn();
        this.setDisclaimerShownAt(data.clientAccountId);
      }

      if (data.investmentQuestionnaire.expired) {
        this.modalService.openInvestmentQuestionaireExpired()
      }
    } else {
      this.logRequestError('Failed to validate current user data response.');
    }
  }

  public fetchClientPortfoliosAndStrategies(clientAccountId: number): void {
    this.restApiService.getPortfoliosAndStrategies({clientId: clientAccountId})
    .subscribe({
      next: this.handlePortfoliosAndStrategiesData.bind(this),
      error: this.logRequestError.bind(this),
    });
  }

  private handlePortfoliosAndStrategiesData(data: ExpectedOwnedStrategy[]) {
    this.userService.hasAnyPortfolioOrStrategy = !!data?.length;
  }

  private logRequestError(error?: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
  }

  private backendError(error?: unknown): void {
    const apiErrorCodes = [
      'E001', // auth successfull but without rights
      'E016', // not auth by BCAS
      'E017', // not in db/wrong gained oauth data
    ];
    const apiStatusCodes = [
      401,
      422,
    ];

    const parsedError = parseToError(error);

    if(parsedError &&
      apiStatusCodes.includes(parsedError.status) &&
      apiErrorCodes.includes(parsedError.error.errorCode)
    ) {
      if(parsedError.error.idToken) {
        this.logoutService.finishLogout({
          idToken: parsedError.error.idToken,
          displayErrorOnRedirectBack: 'client',
        });
      } else {
        this.logoutService.finishLogout({displayErrorOnRedirectBack: 'client'});
      }
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.authError',
        message: 'toastNotifications.messages.authError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      this.router.navigate(['/login']);
    }

    if(isInputError(error, 401)) {
        this.logoutService.finishLogout({displayErrorOnRedirectBack: 'client'});
      }

    this.redirectPreloaderService.redirectingPreloader = false;
  };
}
